import { useState, useEffect, useCallback, useRef } from 'react'
import { apiClient } from '@/services/api'
import { useErrorHandler } from '@/hooks/useAuth'

export interface CheckRegenerateErrorsResponse {
  status: boolean
}

interface UseCheckRegenerateErrorsReturn {
  hasErrorCodes: boolean
  isLoading: boolean
  error: Nullable<string>
  refetch: () => Promise<void>
}

export function useCheckRegenerateErrors(): UseCheckRegenerateErrorsReturn {
  const [hasErrorCodes, setHasErrorCodes] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<Nullable<string>>(null)
  const { handleError } = useErrorHandler()
  const handleErrorRef = useRef(handleError)

  useEffect(() => {
    handleErrorRef.current = handleError
  }, [handleError])

  const checkErrors = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await apiClient.get<CheckRegenerateErrorsResponse>(
        '/qr-codes/check-regenerate-errors'
      )
      setHasErrorCodes(response.data.status)
    } catch (err) {
      setError(handleErrorRef.current(err))
      setHasErrorCodes(false)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    checkErrors()
  }, [checkErrors])

  return {
    hasErrorCodes,
    isLoading,
    error,
    refetch: checkErrors,
  }
}
